/**
 * InspectionPanel — supervisor write + read-only history surface on
 * TaskDetailPage. Read-only for cleaners (their own task) and
 * client_view (own org, no internal notes). Supervisors / contractor_
 * admin / super_admin see the start button + the active-pass
 * completion form.
 *
 * Multi-pass list ordered by pass_number ASC. Each row shows the
 * pass number, status chip, inspector, inspected_at (or started_at),
 * and the failure_reason on non-passed terminal rows. Notes are
 * hidden from client_view at the hook layer.
 */

import { useState, type ReactNode } from 'react'
import { useTranslation }            from 'react-i18next'
import { useInspections, type InspectionRow, type InspectionStatus } from '@/hooks/useInspections'
import { useStartInspection }                                          from '@/hooks/useStartInspection'
import { useCompleteInspection, type CompleteInspectionDecision }      from '@/hooks/useCompleteInspection'
import { isAppError }                                                  from '@/lib/errors/AppError'

interface Props {
  taskId:    string
  /** True only when the viewer can start/complete inspections (supervisor+). */
  canWrite:  boolean
}

const DECISION_OPTIONS: Array<{ value: CompleteInspectionDecision; label: string }> = [
  { value: 'passed',                    label: 'Passed' },
  { value: 'failed',                    label: 'Failed' },
  { value: 're_clean_required',         label: 'Re-clean required' },
  { value: 'blocked_by_maintenance',    label: 'Blocked by maintenance' },
  { value: 'blocked_by_owner_approval', label: 'Blocked by owner approval' },
  { value: 'cancelled',                 label: 'Cancelled' },
]

export function InspectionPanel({ taskId, canWrite }: Props): ReactNode {
  const { t } = useTranslation()
  const { data, isLoading } = useInspections({ cleaningTaskId: taskId })
  const start = useStartInspection()

  const passes      = data ?? []
  const activePass  = passes.find((p) => p.status === 'in_progress')

  if (!canWrite && passes.length === 0 && !isLoading) return null

  return (
    <section
      data-testid="inspection-panel"
      aria-labelledby="inspection-panel-heading"
      style={{
        background:    'var(--brand-white)',
        border:        '1px solid var(--brand-neutral-300)',
        borderRadius:  'var(--brand-radius-lg)',
        padding:       '1rem',
        display:       'flex',
        flexDirection: 'column',
        gap:           '0.6rem',
      }}
    >
      <h2 id="inspection-panel-heading" style={{ margin: 0, fontSize: '1rem' }}>
        {t('inspection.heading', 'Inspection history')}
      </h2>

      {passes.length > 0 && (
        <ul data-testid="inspection-pass-list" style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {passes.map((p) => (
            <PassRow
              key={p.id}
              row={p}
              activeWrite={canWrite && p.status === 'in_progress'}
              taskId={taskId}
            />
          ))}
        </ul>
      )}

      {canWrite && !activePass && (
        <button
          type="button"
          data-testid="inspection-start"
          onClick={() => { void start.mutateAsync({ cleaningTaskId: taskId }) }}
          disabled={start.isPending}
          style={{
            alignSelf:    'flex-start',
            padding:      '0.45rem 0.9rem',
            background:   start.isPending ? 'var(--brand-neutral-300)' : 'var(--brand-primary)',
            color:        'var(--brand-white)',
            border:       'none',
            borderRadius: 'var(--brand-radius-sm)',
            fontSize:     '0.85rem',
            fontWeight:   600,
            cursor:       start.isPending ? 'not-allowed' : 'pointer',
          }}
        >
          {passes.length === 0
            ? t('inspection.startFirst', 'Start inspection')
            : t('inspection.startReinspection', 'Start re-inspection')}
        </button>
      )}
      {start.error && isAppError(start.error) && (
        <p role="alert" data-testid="inspection-start-error" style={{ color: 'var(--brand-danger)', fontSize: '0.85rem', margin: 0 }}>
          {start.error.userMessage}
        </p>
      )}
    </section>
  )
}

function PassRow(
  { row, activeWrite, taskId }: { row: InspectionRow; activeWrite: boolean; taskId: string },
): ReactNode {
  return (
    <li
      data-testid="inspection-pass-row"
      data-inspection-id={row.id}
      data-status={row.status}
      data-pass-number={row.pass_number}
      style={{
        display:       'flex',
        flexDirection: 'column',
        gap:           '0.3rem',
        padding:       '0.5rem 0',
        borderBottom:  '1px solid var(--brand-neutral-100)',
      }}
    >
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline' }}>
        <strong style={{ fontSize: '0.9rem' }}>Pass #{row.pass_number}</strong>
        <StatusChip status={row.status} />
        {row.inspected_at && (
          <span style={{ color: 'var(--brand-neutral-500)', fontSize: '0.8rem' }}>
            {new Date(row.inspected_at).toLocaleString()}
          </span>
        )}
      </div>
      {row.failure_reason && (
        <p data-testid="inspection-failure-reason" style={{ margin: 0, fontSize: '0.85rem', color: 'var(--brand-neutral-700)' }}>
          {row.failure_reason}
        </p>
      )}
      {row.notes && (
        <p data-testid="inspection-notes" style={{ margin: 0, fontSize: '0.8rem', color: 'var(--brand-neutral-500)', fontStyle: 'italic' }}>
          {row.notes}
        </p>
      )}
      {activeWrite && <CompleteForm inspectionId={row.id} taskId={taskId} />}
    </li>
  )
}

function StatusChip({ status }: { status: InspectionStatus }): ReactNode {
  return (
    <span
      data-testid="inspection-status-chip"
      data-status={status}
      style={{
        display:      'inline-block',
        padding:      '0.1rem 0.45rem',
        borderRadius: 'var(--brand-radius-full)',
        background:   'var(--brand-neutral-100)',
        color:        'var(--brand-neutral-700)',
        fontSize:     '0.7rem',
        fontWeight:   600,
      }}
    >
      {status.replace(/_/g, ' ')}
    </span>
  )
}

function CompleteForm({ inspectionId, taskId }: { inspectionId: string; taskId: string }): ReactNode {
  const { t } = useTranslation()
  const complete = useCompleteInspection()
  const [decision, setDecision]             = useState<CompleteInspectionDecision>('passed')
  const [failureReason, setFailureReason]   = useState('')
  const [notes, setNotes]                   = useState('')

  const reasonRequired = decision !== 'passed'
  const reasonValid    = !reasonRequired
    || (failureReason.trim().length > 0 && failureReason.length <= 1000)
  const notesValid     = notes.length <= 1000

  async function submit(): Promise<void> {
    if (!reasonValid || !notesValid) return
    try {
      await complete.mutateAsync({
        inspectionId,
        cleaningTaskId:    taskId,
        decision,
        failureReason:     reasonRequired ? failureReason.trim() : undefined,
        notes:             notes.length > 0 ? notes : undefined,
      })
      setFailureReason('')
      setNotes('')
      setDecision('passed')
    } catch { /* error rendered via complete.error */ }
  }

  const errMsg = complete.error && isAppError(complete.error) ? complete.error.userMessage : null

  return (
    <div
      data-testid="inspection-complete-form"
      style={{
        marginTop:      '0.5rem',
        display:        'flex',
        flexDirection:  'column',
        gap:            '0.5rem',
        padding:        '0.6rem',
        background:     'var(--brand-neutral-50, var(--brand-neutral-100))',
        borderRadius:   'var(--brand-radius-sm)',
      }}
    >
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
        <span style={{ fontSize: '0.8rem', color: 'var(--brand-neutral-700)' }}>
          {t('inspection.decisionLabel', 'Decision')}
        </span>
        <select
          data-testid="inspection-decision-select"
          value={decision}
          onChange={(e) => setDecision(e.target.value as CompleteInspectionDecision)}
          style={{
            padding:      '0.4rem',
            fontSize:     '0.9rem',
            borderRadius: 'var(--brand-radius-sm)',
            border:       '1px solid var(--brand-neutral-300)',
          }}
        >
          {DECISION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </label>

      {reasonRequired && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--brand-neutral-700)' }}>
            {t('inspection.failureReasonLabel', 'Failure reason (1–1000 chars)')}
          </span>
          <textarea
            data-testid="inspection-failure-reason-input"
            value={failureReason}
            onChange={(e) => setFailureReason(e.target.value.slice(0, 1000))}
            rows={2}
            maxLength={1000}
            style={{
              fontSize:     '0.9rem',
              padding:      '0.4rem',
              borderRadius: 'var(--brand-radius-sm)',
              border:       '1px solid var(--brand-neutral-300)',
              resize:       'vertical',
            }}
          />
        </label>
      )}

      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
        <span style={{ fontSize: '0.8rem', color: 'var(--brand-neutral-700)' }}>
          {t('inspection.notesLabel', 'Internal notes (optional, ≤1000 chars)')}
        </span>
        <textarea
          data-testid="inspection-notes-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value.slice(0, 1000))}
          rows={2}
          maxLength={1000}
          style={{
            fontSize:     '0.9rem',
            padding:      '0.4rem',
            borderRadius: 'var(--brand-radius-sm)',
            border:       '1px solid var(--brand-neutral-300)',
            resize:       'vertical',
          }}
        />
      </label>

      <button
        type="button"
        data-testid="inspection-complete-submit"
        onClick={() => { void submit() }}
        disabled={complete.isPending || !reasonValid || !notesValid}
        style={{
          alignSelf:    'flex-start',
          padding:      '0.4rem 0.8rem',
          background:   complete.isPending || !reasonValid || !notesValid
                          ? 'var(--brand-neutral-300)'
                          : 'var(--brand-primary)',
          color:        'var(--brand-white)',
          border:       'none',
          borderRadius: 'var(--brand-radius-sm)',
          fontSize:     '0.85rem',
          fontWeight:   600,
          cursor:       complete.isPending || !reasonValid || !notesValid ? 'not-allowed' : 'pointer',
        }}
      >
        {t('inspection.submit', 'Submit decision')}
      </button>
      {errMsg && (
        <p role="alert" data-testid="inspection-complete-error" style={{ color: 'var(--brand-danger)', fontSize: '0.85rem', margin: 0 }}>
          {errMsg}
        </p>
      )}
    </div>
  )
}
